import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CameraAlt,
  Close,
  CloudUpload,
} from '@mui/icons-material';

interface ImageUploadProps {
  currentImage?: string;
  onImageChange: (file: File) => void;
  // onImageChange: (file: File | null) => void;
  type: 'profile' | 'banner';
  disabled?: boolean;
  isUploadingToIPFS?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  onImageChange,
  type,
  disabled = false,
  isUploadingToIPFS = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadstart = () => setIsUploading(true);
    reader.onloadend = () => setIsUploading(false);
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setPreview(result);
      onImageChange(file);
    };
    reader.readAsDataURL(file);
  };

  // const handleRemoveImage = () => {
  //   setPreview(null);
  //   onImageChange(null);
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = '';
  //   }
  // };

  const handleRemoveImage = () => {
  setPreview(null);
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
   }
  };


  const handleClick = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  const displayImage = preview || currentImage;

  if (type === 'banner') {
    return (
      <Box
        sx={{
          position: 'relative',
          height: 200,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          background: displayImage 
            ? `url(${displayImage}) center/cover`
            : theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': disabled ? {} : {
            '& .upload-overlay': {
              opacity: 1,
            },
          },
        }}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: 'none' }}
          disabled={disabled}
        />
        
        <Box
    className="upload-overlay"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity 0.3s',
    }}
  >
    {isUploadingToIPFS ? (
      <Box sx={{ textAlign: 'center', color: 'white' }}>
        <CircularProgress size={40} sx={{ color: 'white', mb: 1 }} />
        <Typography variant="caption">Uploading...</Typography>
      </Box>
    ) : isUploading ? (
      <CircularProgress size={40} sx={{ color: 'white' }} />
    ) : (
      <CameraAlt sx={{ fontSize: 40, color: 'white' }} />
    )}
  </Box>
        
        {displayImage && !disabled && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveImage();
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.9)',
              },
            }}
            size="small"
          >
            <Close />
          </IconButton>
        )}
      </Box>
    );
  }

  // Profile image upload
  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
        disabled={disabled}
      />
      
      <Box
        sx={{
          position: 'relative',
          width: 150,
          height: 150,
          borderRadius: '50%',
          overflow: 'hidden',
          border: `4px solid ${theme.palette.primary.main}`,
          background: displayImage 
            ? `url(${displayImage}) center/cover`
            : theme.palette.background.paper,
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': disabled ? {} : {
            '& .upload-overlay': {
              opacity: 1,
            },
          },
        }}
        onClick={handleClick}
      >
        {!displayImage && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                : 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',
            }}
          >
            <CameraAlt sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
          </Box>
        )}
        
        <Box
    className="upload-overlay"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity 0.3s',
    }}
  >
    {isUploadingToIPFS ? (
      <Box sx={{ textAlign: 'center', color: 'white' }}>
        <CircularProgress size={40} sx={{ color: 'white', mb: 1 }} />
        <Typography variant="caption">Uploading...</Typography>
      </Box>
    ) : isUploading ? (
      <CircularProgress size={40} sx={{ color: 'white' }} />
    ) : (
      <CameraAlt sx={{ fontSize: 40, color: 'white' }} />
    )}
  </Box>
      </Box>
      
      {!disabled && (
        <IconButton
          onClick={handleClick}
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            background: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              background: theme.palette.primary.dark,
            },
          }}
          size="small"
        >
          <CloudUpload />
        </IconButton>
      )}
    </Box>
  );
};

export default ImageUpload;